import { createConsentControlledVendor, readConfiguredValue } from './consentControlledVendor'

const GOOGLE_TAG_SCRIPT_ATTRIBUTE = 'data-professionals-google-tag'

interface GoogleTagConfiguration {
  googleAdsId?: string
  ga4MeasurementId?: string
  primaryId: string
}

function createGtagQueue() {
  const dataLayer = (window.dataLayer ??= [])
  window.gtag = function gtag() {
    // gtag.js distinguishes its canonical Arguments entries from plain arrays.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
  })
}

function resolveConfiguration(): GoogleTagConfiguration | undefined {
  const googleAdsId = readConfiguredValue(import.meta.env.VITE_GOOGLE_ADS_ID)
  const ga4MeasurementId = readConfiguredValue(import.meta.env.VITE_GA4_MEASUREMENT_ID)
  const primaryId = googleAdsId ?? ga4MeasurementId
  if (!primaryId) return undefined

  return { googleAdsId, ga4MeasurementId, primaryId }
}

function resolveScriptUrl(configuration: GoogleTagConfiguration) {
  const gatewayUrl = readConfiguredValue(import.meta.env.VITE_GOOGLE_TAG_GATEWAY_URL)
  if (gatewayUrl) {
    try {
      const resolvedGatewayUrl = new URL(gatewayUrl, window.location.origin)
      if (resolvedGatewayUrl.origin === window.location.origin) {
        return `${resolvedGatewayUrl.href.replace(/\/$/, '')}/`
      }
    } catch {
      // Ignore malformed gateway configuration and fall back to Google's loader.
    }
  }

  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(configuration.primaryId)}`
}

const googleTag = createConsentControlledVendor({
  scriptAttribute: GOOGLE_TAG_SCRIPT_ATTRIBUTE,
  resolveConfiguration,
  isQueueAvailable: () => typeof window.gtag === 'function',
  createQueue: createGtagQueue,
  grantConsent: () => {
    window.gtag?.('consent', 'update', {
      ad_storage: 'granted',
      analytics_storage: 'granted',
    })
  },
  revokeConsent: () => {
    window.gtag?.('consent', 'update', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
    })
  },
  initialize: ({ googleAdsId, ga4MeasurementId }) => {
    if (googleAdsId) window.gtag?.('config', googleAdsId)
    if (ga4MeasurementId) {
      window.gtag?.('config', ga4MeasurementId, { send_page_view: false })
    }
  },
  resolveScriptUrl,
})

export const configureGoogleTag = googleTag.configure
export const revokeGoogleTagConsent = googleTag.revokeConsent
